import { create } from 'zustand';
import {
  getNotificationPreferences,
  updateNotificationPreferences,
  NotificationPreferences,
  getGoogleCalendars,
  syncGoogleCalendars,
  updateCalendarSettings,
  GoogleCalendar,
  getSubscribedCalendars,
  addSubscribedCalendar,
  updateSubscribedCalendar,
  deleteSubscribedCalendar,
  syncAllSubscribedCalendars,
  SubscribedCalendar,
  getMicrosoftCalendars,
  syncMicrosoftCalendars,
  getMicrosoftConnectionStatus,
  updateMicrosoftCalendarSettings,
  MicrosoftCalendar,
  MicrosoftConnectionStatus,
  syncSubscribedCalendar,
} from '../services';
import { clearBriefingCache } from '../services/briefingApi';

interface SettingsState {
  // State
  preferences: NotificationPreferences | null;
  googleCalendars: GoogleCalendar[];
  microsoftCalendars: MicrosoftCalendar[];
  subscribedCalendars: SubscribedCalendar[];
  microsoftStatus: MicrosoftConnectionStatus | null;
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;
  lastFetchedAt: number | null;
  /**
   * Bumped once after every successful calendar enable/disable PATCH (Google
   * or Microsoft). Schedule readers that stay mounted while the settings sheet
   * is open (Now, week, month) list it in their load effects so they refetch.
   * Monotonic: never reset, so a reset cannot hand a reader a value it has
   * already seen.
   */
  calendarSettingsVersion: number;

  // Actions - Fetch
  fetchAll: () => Promise<void>;
  fetchPreferences: () => Promise<void>;
  fetchGoogleCalendars: () => Promise<void>;
  fetchMicrosoftCalendars: () => Promise<void>;
  fetchSubscribedCalendars: () => Promise<void>;
  fetchMicrosoftStatus: () => Promise<void>;

  // Actions - Preferences
  updatePreference: (key: keyof NotificationPreferences, value: boolean | string | number) => Promise<void>;

  // Actions - Google Calendars
  syncGoogle: () => Promise<void>;
  toggleGoogleCalendar: (calendar: GoogleCalendar) => Promise<void>;

  // Actions - Microsoft Calendars
  syncMicrosoft: () => Promise<void>;
  toggleMicrosoftCalendar: (calendar: MicrosoftCalendar) => Promise<void>;

  // Actions - Subscribed Calendars
  addSubscribed: (name: string, url: string, familyMemberId?: number) => Promise<SubscribedCalendar>;
  updateSubscribed: (id: string, data: Partial<SubscribedCalendar>) => Promise<void>;
  removeSubscribed: (id: string) => Promise<void>;
  syncSubscribed: () => Promise<void>;
  retrySubscribed: (id: string) => Promise<void>;

  // Utility
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  preferences: null,
  googleCalendars: [],
  microsoftCalendars: [],
  subscribedCalendars: [],
  microsoftStatus: null,
  isLoading: false,
  isSyncing: false,
  error: null,
  lastFetchedAt: null,
};

const STALE_AFTER = 5 * 60 * 1000; // 5 minutes

/**
 * Bookkeeping for the saves of one notification preference while any is in
 * flight. Tickets are monotonic (value equality cannot tell A -> B -> A twins
 * apart).
 * - `confirmed`: the value the server is known to hold. Seeded from the key's
 *   current value when a save starts with none of the key in flight; advanced
 *   only by a success whose ticket beats every earlier success.
 * - `inflight`: saves of the key not yet settled.
 * - `latest`: the newest save's ticket; `latestSettled` once it has settled.
 * - `bestOk`: the highest ticket that has succeeded (0 for none).
 * While the latest save is pending the key keeps its optimistic value; once it
 * has settled the key shows `confirmed`, re-derived at every later settle.
 * The entry is deleted when `inflight` returns to 0, so the next chain
 * re-seeds from whatever the key holds then.
 */
interface PreferenceSaveChain {
  confirmed: NotificationPreferences[keyof NotificationPreferences];
  inflight: number;
  latest: number;
  latestSettled: boolean;
  bestOk: number;
}
const preferenceSaveChains = new Map<string, PreferenceSaveChain>();
let preferenceSaveSeq = 0;

export const useSettingsStore = create<SettingsState>()((set, get) => {
  // The one path a successful calendar switch takes: drop the browser-held
  // daily briefings, then tell mounted readers to refetch. A failed PATCH
  // never reaches it.
  const calendarSwitched = () => {
    clearBriefingCache();
    set((state) => ({ calendarSettingsVersion: state.calendarSettingsVersion + 1 }));
  };

  return {
  ...initialState,
  calendarSettingsVersion: 0,

  fetchAll: async () => {
    const { lastFetchedAt, preferences } = get();
    const hasData = preferences !== null;
    const isFresh = lastFetchedAt !== null && Date.now() - lastFetchedAt < STALE_AFTER;

    if (hasData && isFresh) return;
    if (!hasData) set({ isLoading: true, error: null });

    try {
      const [prefs, google, microsoft, subscribed, msStatus] = await Promise.allSettled([
        getNotificationPreferences(),
        getGoogleCalendars(),
        getMicrosoftCalendars(),
        getSubscribedCalendars(),
        getMicrosoftConnectionStatus(),
      ]);

      const prefsOk = prefs.status === 'fulfilled';
      const current = get();
      set({
        preferences: prefsOk ? prefs.value : current.preferences,
        googleCalendars: google.status === 'fulfilled' ? google.value : current.googleCalendars,
        microsoftCalendars: microsoft.status === 'fulfilled' ? microsoft.value : current.microsoftCalendars,
        subscribedCalendars: subscribed.status === 'fulfilled' ? subscribed.value : current.subscribedCalendars,
        microsoftStatus: msStatus.status === 'fulfilled' ? msStatus.value : current.microsoftStatus,
        isLoading: false,
        // Only mark fresh if preferences (the critical piece) loaded
        lastFetchedAt: prefsOk ? Date.now() : current.lastFetchedAt,
        error: prefsOk ? null : 'Failed to load settings',
      });
    } catch (err) {
      if (!hasData) {
        set({
          error: err instanceof Error ? err.message : 'Failed to load settings',
          isLoading: false,
        });
      }
    }
  },

  fetchPreferences: async () => {
    const preferences = await getNotificationPreferences();
    set({ preferences });
  },

  fetchGoogleCalendars: async () => {
    const calendars = await getGoogleCalendars();
    set({ googleCalendars: calendars });
  },

  fetchMicrosoftCalendars: async () => {
    const calendars = await getMicrosoftCalendars();
    set({ microsoftCalendars: calendars });
  },

  fetchSubscribedCalendars: async () => {
    const calendars = await getSubscribedCalendars();
    set({ subscribedCalendars: calendars });
  },

  fetchMicrosoftStatus: async () => {
    const status = await getMicrosoftConnectionStatus();
    set({ microsoftStatus: status });
  },

  // Preferences
  updatePreference: async (key, value) => {
    const { preferences } = get();
    if (!preferences) return;

    // Optimistic update, key-scoped: only `key` changes, read from the state
    // at the moment of the write.
    const ticket = ++preferenceSaveSeq;
    const chain: PreferenceSaveChain = preferenceSaveChains.get(key)
      ?? { confirmed: preferences[key], inflight: 0, latest: 0, latestSettled: false, bestOk: 0 };
    preferenceSaveChains.set(key, chain);
    chain.inflight += 1;
    chain.latest = ticket;
    chain.latestSettled = false;
    set((state) => (state.preferences
      ? { preferences: { ...state.preferences, [key]: value } }
      : {}));

    // Settle this save: a success that beats every earlier success advances
    // `confirmed`; once the latest save has settled the key shows `confirmed`
    // (never this save's own prior, which may be an older optimistic value);
    // the chain is dropped when no save of the key is left in flight.
    const settle = (ok: boolean) => {
      chain.inflight -= 1;
      if (ok && ticket > chain.bestOk) {
        chain.confirmed = value;
        chain.bestOk = ticket;
      }
      if (ticket === chain.latest) chain.latestSettled = true;
      if (chain.latestSettled) {
        const shown = chain.confirmed;
        set((state) => (state.preferences
          ? { preferences: { ...state.preferences, [key]: shown } }
          : {}));
      }
      if (chain.inflight === 0) preferenceSaveChains.delete(key);
    };

    try {
      await updateNotificationPreferences({ [key]: value });
    } catch (err) {
      settle(false);
      throw err;
    }
    settle(true);
  },

  // Google Calendars
  syncGoogle: async () => {
    set({ isSyncing: true });
    try {
      const { calendars } = await syncGoogleCalendars();
      set({ googleCalendars: calendars, isSyncing: false });
    } catch (err) {
      set({ isSyncing: false });
      throw err;
    }
  },

  toggleGoogleCalendar: async (calendar) => {
    const newEnabled = !calendar.is_enabled;
    // Optimistic update
    set((state) => ({
      googleCalendars: state.googleCalendars.map((c) =>
        c.id === calendar.id ? { ...c, is_enabled: newEnabled } : c
      ),
    }));

    try {
      await updateCalendarSettings(calendar.id, { is_enabled: newEnabled });
    } catch (err) {
      // Revert on error
      set((state) => ({
        googleCalendars: state.googleCalendars.map((c) =>
          c.id === calendar.id ? { ...c, is_enabled: !newEnabled } : c
        ),
      }));
      throw err;
    }
    calendarSwitched();
  },

  // Microsoft Calendars
  syncMicrosoft: async () => {
    set({ isSyncing: true });
    try {
      const { calendars } = await syncMicrosoftCalendars();
      set({ microsoftCalendars: calendars, isSyncing: false });
    } catch (err) {
      set({ isSyncing: false });
      throw err;
    }
  },

  toggleMicrosoftCalendar: async (calendar) => {
    const newEnabled = !calendar.is_enabled;
    // Optimistic update
    set((state) => ({
      microsoftCalendars: state.microsoftCalendars.map((c) =>
        c.id === calendar.id ? { ...c, is_enabled: newEnabled } : c
      ),
    }));

    try {
      await updateMicrosoftCalendarSettings(calendar.id, { is_enabled: newEnabled });
    } catch (err) {
      // Revert on error
      set((state) => ({
        microsoftCalendars: state.microsoftCalendars.map((c) =>
          c.id === calendar.id ? { ...c, is_enabled: !newEnabled } : c
        ),
      }));
      throw err;
    }
    calendarSwitched();
  },

  // Subscribed Calendars
  addSubscribed: async (name, url, familyMemberId) => {
    const calendar = await addSubscribedCalendar(name, url, undefined, familyMemberId);
    set((state) => ({
      subscribedCalendars: [...state.subscribedCalendars, calendar],
    }));
    return calendar;
  },

  updateSubscribed: async (id, data) => {
    const calendar = await updateSubscribedCalendar(id, data);
    set((state) => ({
      subscribedCalendars: state.subscribedCalendars.map((c) =>
        c.id === id ? calendar : c
      ),
    }));
  },

  removeSubscribed: async (id) => {
    await deleteSubscribedCalendar(id);
    set((state) => ({
      subscribedCalendars: state.subscribedCalendars.filter((c) => c.id !== id),
    }));
  },

  syncSubscribed: async () => {
    set({ isSyncing: true });
    try {
      await syncAllSubscribedCalendars();
      const calendars = await getSubscribedCalendars();
      set({ subscribedCalendars: calendars, isSyncing: false });
    } catch (err) {
      set({ isSyncing: false });
      throw err;
    }
  },

  retrySubscribed: async (id: string) => {
    // Per-calendar: the server clears the pause and judges the attempt fresh.
    // The SYNC outcome is the story; the reload is best-effort bookkeeping —
    // a failed reload must neither replace the sync's friendly error nor
    // turn a successful sync into "Sync failed" (r377).
    let syncError: unknown = null;
    try {
      await syncSubscribedCalendar(id);
    } catch (err) {
      syncError = err;
    }
    try {
      const calendars = await getSubscribedCalendars();
      set({ subscribedCalendars: calendars });
    } catch {
      // Stale list beats a wrong verdict.
    }
    if (syncError) throw syncError;
  },

  // Utility
  clearError: () => set({ error: null }),
  reset: () => set(initialState),
  };
});

// Selector hooks
export const useNotificationPreferences = () => useSettingsStore((state) => state.preferences);
export const useGoogleCalendars = () => useSettingsStore((state) => state.googleCalendars);
export const useMicrosoftCalendars = () => useSettingsStore((state) => state.microsoftCalendars);
export const useSubscribedCalendars = () => useSettingsStore((state) => state.subscribedCalendars);

export const isSettingsStale = () => {
  const { lastFetchedAt } = useSettingsStore.getState();
  return lastFetchedAt === null || Date.now() - lastFetchedAt > STALE_AFTER;
};

export default useSettingsStore;
